import { getApiBaseUrl, getClientUrl } from './api';

let pendingRequest = null;
let cachedResult = null;

export function buildGoogleReviewsUrl() {
  const base = getApiBaseUrl();
  const clientUrl = encodeURIComponent(getClientUrl());
  return `${base}/tsr/google-reviews?ClientUrl=${clientUrl}`;
}

function normalizePayload(data) {
  if (!data) return null;
  const reviews = Array.isArray(data.reviews) ? data.reviews : [];
  if (!reviews.length) return null;

  return {
    rating: Number(data.rating) || 0,
    reviewCount: Number(data.reviewCount) || 0,
    googleMapsUrl: data.googleMapsUrl || '',
    displayName: data.displayName || '',
    reviews,
  };
}

export async function fetchGoogleReviews({ forceRefresh = false } = {}) {
  if (!forceRefresh && cachedResult) {
    return cachedResult;
  }

  if (!forceRefresh && pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = (async () => {
    try {
      const response = await fetch(buildGoogleReviewsUrl());
      const json = await response.json().catch(() => ({}));

      if (!response.ok || !json.success || !json.data) {
        return null;
      }

      const result = normalizePayload(json.data);
      cachedResult = result;
      return result;
    } catch {
      cachedResult = null;
      return null;
    } finally {
      pendingRequest = null;
    }
  })();

  return pendingRequest;
}

export function formatReviewCount(count) {
  const n = Number(count) || 0;
  return n.toLocaleString('en-IN');
}

export function getAuthorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'G';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function getRatingLabel(rating) {
  const value = Number(rating) || 0;
  if (value >= 4.8) return 'Excellent on Google';
  if (value >= 4.5) return 'Great on Google';
  if (value >= 4) return 'Good on Google';
  return 'Rated on Google';
}

export function formatRelativeReviewTime(unixSeconds) {
  const seconds = Number(unixSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';

  const diffSec = Math.floor((Date.now() - seconds * 1000) / 1000);
  if (diffSec < 0) return '';
  if (diffSec < 60) return 'just now';

  const minutes = Math.floor(diffSec / 60);
  if (minutes < 60) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

export function getReviewRelativeTime(review) {
  if (review?.time) {
    const formatted = formatRelativeReviewTime(review.time);
    if (formatted) return formatted;
  }
  return review?.relativeTime || '';
}
